import React from "react";
import classNames from "classnames";

// types
interface FieldRowProps {
  id: string;
  label?: string;
  required?: boolean;
  /** content rendered between the label and the control (e.g. server sub-label) */
  subLabel?: React.ReactNode;
  /** resolved error message, if any */
  error?: string;
  /** optional content rendered after the error (e.g. a help link) */
  belowControl?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The ONE place label / required marker / sub-label / error text / aria wiring live.
 *
 * Every field — scalar or compound — is wrapped once here, so a compound phone
 * (two controls) still gets a single label and a single error slot. Keeping
 * this out of the input primitives is what prevents three drifting label
 * implementations and centralises the a11y association (label htmlFor → control
 * id, error id → aria-describedby on the control).
 *
 * The control is responsible for setting aria-describedby={errorId} itself
 * (DynamicField passes it down); FieldRow renders the error element with that id.
 */
const FieldRow = ({ id, label, required, subLabel, error, belowControl, children }: FieldRowProps) => {
  const errorId = `${id}-error`;
  return (
    <div
      className={classNames("bwp-field", {
        "bwp-field--state-error": Boolean(error),
        "bwp-field--required": required,
      })}
    >
      {label && (
        <label className="bwp-field__label" htmlFor={id}>
          {required && (
            <span className="bwp-field__required" aria-hidden="true">
              *
            </span>
          )}
          {label}
          {!required && <span className="bwp-field__optional"> (optional)</span>}
        </label>
      )}
      {subLabel && <div className="bwp-field__sublabel">{subLabel}</div>}
      {children}
      {error && (
        <p className="bwp-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
      {belowControl}
    </div>
  );
};

export default FieldRow;